import {
  getLocationCompanyContactListData,
  _getLCCDataById,
  getLocationCompanyContactListDataByLocationId,
} from '../locationCompanyContacts/mockData'
import { getLocationHardwareTenantOwnedItemListDataByLocationId } from '../locationHardwareTenantOwnedItems/mockData'
import { getLocationHardwareCompanyOwnedItemListDataByLocationId } from '../locationHardwareCompanyOwnedItems/mockData'
import { getOpportunityDataById } from '../opportunities/mockData'
import { getTaskListDataByLocationId } from '../tasks/mockData'
import { getListDataFunctionFactory } from '../../modules/getListDataFunctionFactory'
import { getLocationDocumentListDataByLocationId } from '../locationDocuments/mockData'
import { getLocationNoteListDataByLocationId } from '../locationNotes/mockData'
import { getCMSSListDataByLocationId } from '../companyMSServices/mockData'
import {
  getLocationTypeListData,
  _getLTSelectItemDataByLabel,
} from './../selectLists/locationTypes/mockData'
import {
  getCompanyListData,
  _getCSelectItemDataById,
  getCompanyDataById,
} from '~/api/msw/api/companies/mockData'
import type { ICompany } from '~/types/company/models'
import type { ILocation, ILocationOverview } from '@/types/location/models'
import type { TEntityId, IEntitySelectItem } from '~/types/models'
import { isInstalledCompanyMSService } from '~/cynqueModules/dataObjects/CompanyMSService'
import { getCountryDataById } from '~/api/msw/api/selectLists/countries/mockData'
import { CountryIds } from '~/services/selectLists/countries'
import {
  getLocationStatusById,
  LocationStatusIds,
} from '~/services/selectLists/locationStatii'

const locationListData = undefined as unknown as ILocation[]

export const getLocationListData = getListDataFunctionFactory<ILocation[]>({
  listData: locationListData,
  storageKey: 'locations',
  getStartingListData: getStartingLocationListData,
  forOfFunction: async (location) => {
    location.numServices = await getLocationNumInstalledServices(location.id)
  },
})

export function _getLDataById(id: TEntityId, list: ILocation[]): ILocation {
  if (!list) {
    throw new Error(`_getLDataById called with undefined/null list`)
  }
  const location = list.find((cct: ILocation) => cct.id === id)
  if (!location) {
    throw new Error(`Can't find location '${id}'`)
  }
  return location
}

export function _getLSelectItemDataById(
  id: TEntityId,
  list: ILocation[]
): IEntitySelectItem {
  if (!list) {
    throw new Error(`_getLSelectItemDataById called with undefined/null list`)
  }
  const location = _getLDataById(id, list)
  return { id: location.id, label: location.nickname }
}

export async function getLocationDataById(id: string): Promise<ILocation> {
  const list: ILocation[] = await getLocationListData()
  return _getLDataById(id, list)
}

export async function getLocationListDataByCompanyId(
  id: TEntityId
): Promise<ILocation[]> {
  const list: ILocation[] = await getLocationListData()
  if (!list) {
    throw new Error(`locationList returned undefined/null`)
  }
  const filteredList = list.filter((loc) => loc.company.id === id) ?? []
  if (!filteredList) {
    throw new Error(`company locationList returned undefined/null`)
  }
  return filteredList
}

export async function getLocationSelectItemDataById(
  id: string
): Promise<IEntitySelectItem | undefined> {
  const location = await getLocationDataById(id)
  if (!location) {
    return undefined
  }
  return { id: location.id, label: location.nickname }
}

export async function getLocationNumInstalledServices(
  id: string
): Promise<number> {
  const cmssListRaw = await getCMSSListDataByLocationId(id)
  const cmssList = cmssListRaw.filter((cmss) =>
    isInstalledCompanyMSService(cmss)
  )
  return cmssList?.length ?? 0
}

export async function getLocationOverviewDataForId(
  id: string
): Promise<ILocationOverview | undefined> {
  const theLocationData = await getLocationDataById(id)
  if (!theLocationData) {
    throw new Error(`Location data for ${id} not found`)
  }
  // Every state, as the API's location_overviews returns them: this mock used
  // to send installed services only, so a Location page in mock mode showed
  // less than the real one (on staging it also lists In Review and Ready for
  // Contract services).
  const INSTALLED_COMPANY_MSSERVICES_ONLY = false
  const DO_INCLUDE_WHOLE_KITS = true
  const theOverviewData = {
    location: theLocationData,
    companyMSServices:
      (await getCMSSListDataByLocationId(
        id,
        INSTALLED_COMPANY_MSSERVICES_ONLY,
        DO_INCLUDE_WHOLE_KITS
      )) ?? [],
    locationHardwareTenantOwnedItems:
      (await getLocationHardwareTenantOwnedItemListDataByLocationId(id)) ?? [],
    locationHardwareCompanyOwnedItems:
      (await getLocationHardwareCompanyOwnedItemListDataByLocationId(id)) ?? [],
    locationCompanyContacts:
      (await getLocationCompanyContactListDataByLocationId(id)) ?? [],
    locationDocuments:
      (await getLocationDocumentListDataByLocationId(id)) ?? [],
    locationNotes: (await getLocationNoteListDataByLocationId(id)) ?? [],
    tasks: (await getTaskListDataByLocationId(id)) ?? [],
    opportunities: [
      await getOpportunityDataById('XXO0001'),
      await getOpportunityDataById('XXO0003'),
    ],
  }
  return theOverviewData
}

export async function getCompanyDataByLocationId(
  id: string
): Promise<ICompany | undefined> {
  const location = await getLocationDataById(id)
  if (!location) {
    return undefined
  }
  const company = await getCompanyDataById(location.company.id)
  if (!company) {
    return undefined
  }
  return company
}

export async function getStartingLocationListData(): Promise<ILocation[]> {
  const cList = await getCompanyListData()
  const lccList = await getLocationCompanyContactListData()
  const ltList = await getLocationTypeListData()
  const unitedStates = await getCountryDataById(CountryIds.UNITED_STATES)
  const france = await getCountryDataById(CountryIds.FRANCE)
  const philippines = await getCountryDataById(CountryIds.PHILIPPINES)
  const germany = await getCountryDataById(CountryIds.GERMANY)
  const switzerland = await getCountryDataById(CountryIds.SWITZERLAND)
  const italy = await getCountryDataById(CountryIds.ITALY)
  const unitedKingdom = await getCountryDataById(CountryIds.UNITED_KINGDOM)
  const canada = await getCountryDataById(CountryIds.CANADA)
  const hongkong = await getCountryDataById(CountryIds.HONG_KONG_SAR)
  const mexico = await getCountryDataById(CountryIds.MEXICO)
  const india = await getCountryDataById(CountryIds.INDIA)
  const singapore = await getCountryDataById(CountryIds.SINGAPORE)
  const blah = await getCountryDataById(10)

  return [
    {
      id: 'XXL0001',
      hrid: 'L0001',
      company: _getCSelectItemDataById('XXC0001', cList),
      nickname: 'Boston',
      mainPhone: '510-444-9999',
      address: {
        country: mexico,
        addressLine1: '143 Somewhere St',
        addressLine2: 'Suite 123A',
        city: 'Boston',
        state: {
          id: 21,
          label: 'MA',
        },
        zip: '04359',
      },
      type: _getLTSelectItemDataByLabel('Branch', ltList),
      primaryLcon: _getLCCDataById('XXLCC0001', lccList),
      secondaryLcon: _getLCCDataById('XXLCC0003', lccList),
      hours: 'M-F 7am-6pm',
      dmarcLocation: 'MDF Room B, 2nd Floor',
      specialAccessRequirements: 'Check in at front desk, photo ID required',
      lec: 'Verizon',
      cableCompany: 'Comcast',
      hasLitFiber: true,
      gpsCoordinates: '42.3601,-71.0589',
      costCenter: 'CC-4100',
      spend: 4500,
      numServices: 0,
      status: getLocationStatusById(LocationStatusIds.ACTIVE)!,
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXL0002',
      hrid: 'L0002',
      company: _getCSelectItemDataById('XXC0001', cList),
      nickname: 'Big Mac',
      mainPhone: '510-666-8877',
      address: {
        country: philippines,
        addressLine1: '567 Somewhere Else St',
        city: 'Boston',
        state: {
          id: 21,
          label: 'MA',
        },
        zip: '04359',
      },
      type: _getLTSelectItemDataByLabel('Branch', ltList),
      primaryLcon: _getLCCDataById('XXLCC0004', lccList),
      secondaryLcon: _getLCCDataById('XXLCC0005', lccList),
      hours: 'M-F 8am-5pm',
      dmarcLocation: 'Telecom closet, Suite 200',
      specialAccessRequirements: null,
      lec: 'Verizon',
      cableCompany: 'Comcast',
      hasLitFiber: true,
      gpsCoordinates: '42.3554,-71.0640',
      costCenter: 'CC-4100',
      spend: 2800,
      numServices: 0,
      status: getLocationStatusById(LocationStatusIds.ACTIVE)!,
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXL0003',
      hrid: 'L0003',
      company: _getCSelectItemDataById('XXC0001', cList),
      nickname: 'Target',
      mainPhone: '510-876-9876',
      address: {
        country: germany,
        addressLine1: '897 Nowhere St',
        city: 'Boston',
        state: {
          id: 21,
          label: 'MA',
        },
        zip: '04359',
      },
      type: _getLTSelectItemDataByLabel('Branch', ltList),
      primaryLcon: _getLCCDataById('XXLCC0006', lccList),
      secondaryLcon: _getLCCDataById('XXLCC0007', lccList),
      hours: '24/7',
      dmarcLocation: 'Basement demarc room',
      specialAccessRequirements: 'Escort required, call 30 min ahead',
      lec: 'Lumen',
      cableCompany: null,
      hasLitFiber: false,
      gpsCoordinates: '42.3412,-71.0712',
      costCenter: 'CC-5200',
      spend: 12000,
      numServices: 0,
      status: getLocationStatusById(LocationStatusIds.ACTIVE)!,
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXL0004',
      hrid: 'L0004',
      company: _getCSelectItemDataById('XXC0001', cList),
      nickname: 'Lake',
      mainPhone: '510-222-1000',
      address: {
        country: switzerland,
        addressLine1: '1468 Hope Ave',
        city: 'Boston',
        state: {
          id: 21,
          label: 'MA',
        },
        zip: '04359',
      },
      type: _getLTSelectItemDataByLabel('Branch', ltList),
      primaryLcon: _getLCCDataById('XXLCC0008', lccList),
      secondaryLcon: _getLCCDataById('XXLCC0009', lccList),
      hours: 'M-F 9am-5pm',
      dmarcLocation: null,
      specialAccessRequirements: null,
      lec: 'Lumen',
      cableCompany: 'Spectrum',
      hasLitFiber: false,
      gpsCoordinates: '42.3489,-71.0803',
      costCenter: null,
      spend: 1500,
      numServices: 0,
      status: getLocationStatusById(LocationStatusIds.ACTIVE)!,
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXL0005',
      hrid: 'L0005',
      company: _getCSelectItemDataById('XXC0009', cList),
      address: {
        country: italy,
        addressLine1: '69 Milk Street',
        addressLine2: 'Suite 300',
        city: 'Westborough',
        state: { id: 21, label: 'MA' },
        zip: '01581',
      },
      nickname: 'The Cave',
      mainPhone: '(555) (555) 5555',
      type: _getLTSelectItemDataByLabel('HQ', ltList),
      primaryLcon: undefined,
      secondaryLcon: undefined,
      hours: '24/7',
      dmarcLocation: 'MDF Room 101, Ground Floor',
      specialAccessRequirements: 'Biometric access badge required',
      lec: 'Windstream',
      cableCompany: 'Comcast',
      hasLitFiber: true,
      gpsCoordinates: '42.2695,-71.8078',
      costCenter: 'CC-3000',
      spend: 8500,
      numServices: 0,
      createdAt: '2024-01-04T20:46:00.000Z',
      modifiedAt: '2024-01-04T20:46:00.000Z',
      status: getLocationStatusById(LocationStatusIds.ACTIVE)!,
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXL0006',
      hrid: 'L0006',
      company: _getCSelectItemDataById('XXC0009', cList),
      address: {
        country: unitedStates,
        addressLine1: '1425 Longhaul Way',
        city: 'Syracuse ',
        state: { id: 32, label: 'NY' },
        zip: '14785',
      },
      nickname: 'W5607',
      mainPhone: '444-444-4444',
      type: _getLTSelectItemDataByLabel('Office', ltList),
      primaryLcon: undefined,
      secondaryLcon: undefined,
      hours: 'M-F 8am-6pm',
      dmarcLocation: 'IDF Closet 3A',
      specialAccessRequirements: null,
      lec: 'Frontier',
      cableCompany: 'Spectrum',
      hasLitFiber: true,
      gpsCoordinates: '43.0481,-76.1474',
      costCenter: 'CC-3010',
      spend: 3200,
      numServices: 0,
      createdAt: '2024-01-04T20:48:00.000Z',
      modifiedAt: '2024-01-04T20:48:00.000Z',
      status: getLocationStatusById(LocationStatusIds.ACTIVE)!,
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXL0007',
      hrid: 'L0007',
      company: _getCSelectItemDataById('XXC0009', cList),
      address: {
        country: unitedStates,
        addressLine1: '85210',
        city: 'Glendale',
        state: { id: 35, label: 'OH' },
        zip: '87456',
      },
      nickname: 'W4852',
      mainPhone: '333-333-3333',
      type: _getLTSelectItemDataByLabel('Office', ltList),
      primaryLcon: undefined,
      secondaryLcon: undefined,
      hours: 'M-Sa 7am-7pm',
      dmarcLocation: 'Loading dock entrance, left wall',
      specialAccessRequirements: 'Hard hat required in server area',
      lec: 'AT&T',
      cableCompany: null,
      hasLitFiber: false,
      gpsCoordinates: '39.6062,-83.8129',
      costCenter: 'CC-3020',
      spend: 2100,
      numServices: 0,
      createdAt: '2024-01-04T20:49:00.000Z',
      modifiedAt: '2024-01-04T20:49:00.000Z',
      status: getLocationStatusById(LocationStatusIds.ACTIVE)!,
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXL0008',
      hrid: 'L0008',
      company: _getCSelectItemDataById('XXC0010', cList),
      address: {
        country: unitedStates,
        addressLine1: '245 Park Ave',
        addressLine2: 'Suite 1800',
        city: 'New York City',
        state: { id: 32, label: 'NY' },
        zip: '10167',
      },
      nickname: 'NYC',
      mainPhone: '222-222-2222',
      type: _getLTSelectItemDataByLabel('Office', ltList),
      primaryLcon: undefined,
      secondaryLcon: undefined,
      hours: '24/7',
      dmarcLocation: 'Suite 1800 telecom room',
      specialAccessRequirements:
        'Building security check-in, 24hr advance notice after hours',
      lec: 'Verizon',
      cableCompany: 'Spectrum',
      hasLitFiber: true,
      gpsCoordinates: '40.7549,-73.9724',
      costCenter: 'CC-7700',
      spend: 15000,
      numServices: 0,
      createdAt: '2024-01-25T22:03:00.000Z',
      modifiedAt: '2024-01-25T22:03:00.000Z',
      status: getLocationStatusById(LocationStatusIds.ACTIVE)!,
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXL0009',
      hrid: 'L0009',
      company: _getCSelectItemDataById('XXC0001', cList),
      nickname: 'Waltham',
      mainPhone: '510-(555) 9999',
      address: {
        country: italy,
        addressLine1: '2001 There St',
        addressLine2: 'Suite 400',
        city: 'Waltham',
        state: {
          id: 21,
          label: 'MA',
        },
        zip: '04359',
      },
      type: _getLTSelectItemDataByLabel('Branch', ltList),
      primaryLcon: _getLCCDataById('XXLCC0001', lccList),
      secondaryLcon: _getLCCDataById('XXLCC0003', lccList),
      hours: 'M-F 8am-5pm',
      dmarcLocation: 'Utility room behind reception',
      specialAccessRequirements: null,
      lec: 'Verizon',
      cableCompany: 'Comcast',
      hasLitFiber: true,
      gpsCoordinates: '42.3765,-74.1713',
      costCenter: 'CC-4100',
      spend: 3800,
      numServices: 0,
      status: getLocationStatusById(LocationStatusIds.ACTIVE)!,
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXL0010',
      hrid: 'L0010',
      company: _getCSelectItemDataById('XXC0001', cList),
      nickname: 'Sunset',
      mainPhone: '510-543-5432',
      address: {
        country: unitedKingdom,
        addressLine1: '2021 1st Ave.',
        addressLine2: undefined,
        city: 'Somerville',
        state: {
          id: 21,
          label: 'MA',
        },
        zip: '02143',
      },
      type: _getLTSelectItemDataByLabel('Branch', ltList),
      primaryLcon: _getLCCDataById('XXLCC0001', lccList),
      secondaryLcon: _getLCCDataById('XXLCC0003', lccList),
      hours: null,
      dmarcLocation: null,
      specialAccessRequirements: null,
      lec: null,
      cableCompany: null,
      hasLitFiber: false,
      gpsCoordinates: null,
      costCenter: null,
      spend: null,
      numServices: 0,
      status: getLocationStatusById(LocationStatusIds.ACTIVE)!,
      version: '1',
      canBeDeleted: true,
    },
  ]
}
